import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { glorifyApiClient } from '../../../services/api/glorify'
import type { MarketsOverviewResponse, MarketMover } from '../../../services/api/glorify'
import { formatCurrency, formatSignedPercent, changeClass, formatTimestamp } from './marketsFormat'
import { type SectionError, toSectionError } from './marketsTheme'
import { SectionErrorBanner, StaleChip } from './marketsShared'

type MoverTab = 'gainers' | 'losers' | 'most_actives'

export default function MoversPage(): React.JSX.Element {
  const navigate = useNavigate()

  const [overview, setOverview] = useState<MarketsOverviewResponse | null>(null)
  const [overviewLoading, setOverviewLoading] = useState(true)
  const [overviewError, setOverviewError] = useState<SectionError | null>(null)
  const [moverTab, setMoverTab] = useState<MoverTab>('gainers')

  const fetchOverview = useCallback(async (): Promise<void> => {
    setOverviewError(null)
    try {
      const resp = await glorifyApiClient.marketsOverview()
      setOverview(resp)
    } catch (err) {
      setOverviewError(toSectionError(err, 'Failed to load movers'))
    } finally {
      setOverviewLoading(false)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchOverview()
  }, [fetchOverview])

  const handleRefresh = (): void => {
    setOverviewLoading(true)
    void fetchOverview()
  }

  // Clicking a mover jumps to the Stock Quote page pre-loaded with that
  // symbol via a query param — StockQuotePage reads it on mount.
  const handleSelectMover = (symbol: string): void => {
    navigate(`/dashboard/markets/stock-quote?symbol=${encodeURIComponent(symbol)}`)
  }

  const movers: MarketMover[] = overview
    ? moverTab === 'gainers'
      ? overview.gainers
      : moverTab === 'losers'
        ? overview.losers
        : overview.most_actives
    : []

  return (
    <>
      <div className="gd-page-header gd-page-header--with-action">
        <div className="gd-page-header__text">
          <h1>Movers</h1>
          <p>Top gainers, losers and most active symbols, powered by Financial Modeling Prep.</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <button className="gd-btn gd-btn--tonal has-icon" onClick={handleRefresh}>
            <span className="material-icons" aria-hidden="true">refresh</span>
            Refresh
          </button>
          {overview && (
            <span className="gd-body-small" style={{ color: 'var(--md-sys-color-outline)' }}>
              Updated {formatTimestamp(overview.as_of)}
            </span>
          )}
        </div>
      </div>

      <SectionErrorBanner error={overviewError} />
      {overview?.stale && <div style={{ marginBottom: 12 }}><StaleChip /></div>}

      <div className="gd-section">
        <div className="gd-segmented" role="group" aria-label="Filter movers">
          {(['gainers', 'losers', 'most_actives'] as const).map((tab) => (
            <button
              key={tab}
              className={`gd-segmented__btn${moverTab === tab ? ' gd-segmented__btn--active' : ''}`}
              onClick={() => setMoverTab(tab)}
              aria-pressed={moverTab === tab}
            >
              {tab === 'gainers' ? 'Gainers' : tab === 'losers' ? 'Losers' : 'Most Active'}
            </button>
          ))}
        </div>

        {overviewLoading ? (
          <div className="gd-empty">
            <div className="gd-spinner" aria-label="Loading movers" />
          </div>
        ) : movers.length === 0 ? (
          <div className="gd-empty">
            <span className="material-icons" aria-hidden="true">bar_chart</span>
            <p>No movers to show.</p>
          </div>
        ) : (
          <div className="gd-table-wrap">
            <table className="gd-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Change %</th>
                  <th>Exchange</th>
                </tr>
              </thead>
              <tbody>
                {movers.map((m) => (
                  <tr
                    key={m.symbol}
                    onClick={() => handleSelectMover(m.symbol)}
                    style={{ cursor: 'pointer' }}
                    tabIndex={0}
                    role="button"
                    aria-label={`View quote for ${m.symbol}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleSelectMover(m.symbol)
                      }
                    }}
                  >
                    <td><b>{m.symbol}</b></td>
                    <td className="gd-cell-muted">{m.name}</td>
                    <td>{formatCurrency(m.price)}</td>
                    <td className={changeClass(m.changes_percentage)}>{formatSignedPercent(m.changes_percentage)}</td>
                    <td className="gd-cell-muted">{m.exchange}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
